<template>
  <b-card no-body class="client-table--invitations">
    <b-card-header class="d-block py-1">
      <b-row>
        <b-col sm="9">
          <b-button
            v-if="accountAccessManager.canManageUsers()"
            variant="primary"
            :disabled="loading"
            class="mb-50 mb-sm-0"
            size="sm"
            @click="addUserInvitationModal"
          >
            <GjIcon
              class="mr-50"
              name="Plus"
            />
            {{ $tk('ClientPortal.Account.Edit.AddInvitation') }}
          </b-button>
          <b-modal
            id="modal-add-user-invitation"
            ref="addUserInvitationModal"
            :title="$tk('ClientPortal.Account.Create.AddNewInvitation')"
            centered
            footer="footer"
          >
            <b-form-group
              class="mb-2"
              :label="$tk('ClientPortal.Account.Create.Email')"
              :invalid-feedback="errors['Email']"
            >
              <b-form-input
                v-model="userModal.user.email"
                type="email"
                placeholder="example@email.com"
                :state="errors['Email'] == null ? null : false"
              />
            </b-form-group>
            <b-form-group
              :label="$tk('ClientPortal.Account.Edit.Role')"
              class="mb-0"
            >
              <div
                v-for="role in userRoles"
                :key="role.id"
              >
                <b-form-radio
                  v-if="role.editable"
                  v-model.number="userModal.user.roleId"
                  class="mb-1"
                  :value="role.id"
                >
                  <div class="text-dark mb-50">
                    {{ role.name }}
                  </div>
                  <span class="text-secondary">
                    {{ role.description }}
                  </span>
                </b-form-radio>
              </div>
            </b-form-group>
            <template #modal-footer="{ cancel }">
              <b-button
                class="m-0"
                size="sm"
                variant="outline-secondary"
                @click="cancel()"
              >
                {{ $tk('ClientPortal.Form.Actions.Cancel') }}
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="m-0 ml-1 float-right"
                :disabled="saving || !isFormComplete"
                @click="createUserInvitation(userModal.user)"
              >
                {{ $tk('ClientPortal.Form.Actions.Save') }}
              </b-button>
            </template>
          </b-modal>
        </b-col>
        <b-col sm="3">
          <b-input-group class="input-group-merge">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="filter-input"
              v-model="filter"
              :disabled="loading"
              type="search"
              :placeholder="$tk('ClientPortal.Form.Search')"
            />
          </b-input-group>
        </b-col>
      </b-row>
    </b-card-header>
    <b-skeleton-table
      v-if="loading"
      :columns="4"
      :rows="10"
    />
    <b-table
      v-if="!loading"
      :items="items"
      :fields="fields"
      :filter="filter"
      class="client-table__data"
      show-empty
      :empty-text="$tk('ClientPortal.Account.Table.NoUsersHaveBeenInvitedYet')"
      :current-page="currentPage"
      :per-page="perPage"
      responsive
    >
      <template #cell(fullName)="data">
        <b-avatar
          :src="data.item.imageUrl"
          variant="white"
          class="mr-50"
        />
        <span>
          {{ data.item.fullName }}
        </span>
      </template>
      <template #cell(status)="data">
        <span class="text-success">{{ data.item.status }}</span>
      </template>
      <template #empty="scope">
        <div class="text-center p-3 client-table__empty">
          <div class="empty__client-table">
            <GjIcon
              size="64px"
              name="AvatarAdd"
            />
            <div class="mb-50">
              {{ scope.emptyText }}
            </div>
            <b-link
              v-if="accountAccessManager.canManageUsers()"
              @click="addUserInvitationModal"
            >
              <GjIcon
                class="mr-50"
                name="Plus"
              /> {{ $tk('ClientPortal.Account.Edit.AddInvitation') }}
            </b-link>
          </div>
        </div>
      </template>
      <template #cell(role)="data">
        <div class="d-flex align-items-center justify-content-between">
          <span>
            {{ data.item.role }}
          </span>
          <b-dropdown
            v-if="data.item.editable && accountAccessManager.canManageUsers()"
            variant="link"
            toggle-class="text-decoration-none"
            no-caret
            button-class="p-0"
            right
          >
            <template v-slot:button-content>
              <GjIcon name="Moreoptions_fill" />
            </template>
            <b-dropdown-item @click="openDeleteUserInvitationModal(data.item, data.index)">
              <GjIcon
                class="mr-50"
                name="Delete"
              />
              {{ $tk('ClientPortal.Common.Delete') }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>
    <b-card-footer
      v-if="items.length > 0"
      class="client-table__footer d-flex"
    >
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        size="sm"
        class="my-0 mr-1"
        hide-goto-end-buttons
      >
        <template #prev-text>
          <GjIcon
            class="mr-25"
            name="ArrowLeft"
          />
        </template>
        <template #next-text>
          <GjIcon
            class="ml-25"
            name="ArrowRight"
          />
        </template>
      </b-pagination>
      <span>
        {{ $tk('ClientPortal.Pagination.Viewing') }} {{ fromCount }}
        {{ $tk('ClientPortal.Pagination.To') }} {{ toCount }}
        {{ $tk('ClientPortal.Pagination.Of') }} {{ totalRows }}
        {{ $tk('ClientPortal.Pagination.Items') }}
      </span>
    </b-card-footer>
    <b-modal
      id="user-remove-invitation-modal"
      ref="removeUserInvitationModal"
      :title="$tk('ClientPortal.Account.Edit.DeleteInvitation')"
      centered
      footer="footer"
    >
      <p>{{ $tk('ClientPortal.Account.Edit.AreYouSureYouWantToDeleteAccountInvitation') }}</p>
      <template #modal-footer="{ cancel }">
        <b-button
          class="m-0"
          size="sm"
          variant="outline-secondary"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="m-0 ml-1 float-right"
          :disabled="saving"
          @click="confirmRemoveInvitation()"
        >
          {{ $tk('ClientPortal.Form.Actions.Delete') }}
        </b-button>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import AccountsService from '@/api/client/accounts.service'
import notification from '@/mixins/toastification'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import { AccountAccessManager } from '@/utils/accessManager/accessManager'

export default {
  components: {
    GjIcon,
  },
  mixins: [notification],
  data() {
    return {
      userModal: {
        user: {},
        index: 0,
      },
      userRoles: [],
      errors: {},
      loading: true,
      saving: false,
      items: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      filter: null,
      removeItem: [],
      removeIndex: 0,
      accountAccessManager: AccountAccessManager,
    }
  },
  computed: {
    fromCount() {
      return (this.currentPage - 1) * this.perPage + 1
    },
    toCount() {
      const toCount = this.fromCount + this.perPage - 1
      return Math.min(toCount, this.totalRows)
    },
    isFormComplete() {
      const { email, roleId } = this.userModal.user

      return email && roleId
    },
    fields() {
      return [
        {
          key: 'fullName',
          label: this.$tk('ClientPortal.Account.Name'),
          sortable: true,
        },
        {
          key: 'email',
          label: this.$tk('ClientPortal.Account.Email'),
          sortable: true,
        },
        {
          key: 'role',
          label: this.$tk('ClientPortal.Account.Role'),
          sortable: true,
        },
      ]
    },
  },
  watch: {
    '$route.params.accountId': 'reloadAccountInvitations',
  },
  async mounted() {
    this.loading = true
    try {
      this.items = await AccountsService.getAccountInvitations(this.$route.params.accountId)
      this.userRoles = await AccountsService.getUserRoles(this.$route.params.accountId)
      this.totalRows = this.items.length
    } catch (err) {
      this.error(err.message)
    } finally {
      this.loading = false
    }
  },
  methods: {
    async reloadAccountInvitations() {
      this.loading = true
      try {
        this.items = await AccountsService.getAccountInvitations(this.$route.params.accountId)
        this.userRoles = await AccountsService.getUserRoles(this.$route.params.accountId)
        this.totalRows = this.items.length
      } catch (err) {
        this.error(err.message)
      } finally {
        this.loading = false
      }
    },
    async createUserInvitation(user) {
      this.saving = true
      this.errors = {}
      try {
        const response = await AccountsService.createAccountUser(user, this.$route.params.accountId)
        if (response.invited) {
          // TODO: When back-end makes changes use response.userModel insted of user
          if (!(this.items.some(e => e.email === user.email))) {
            this.items.unshift(user)
            this.totalRows = this.items.length
            this.success(response.message)
          } else {
            this.error(response.message)
          }
          this.$refs.addUserInvitationModal.hide()
        } else if (response.saved) {
          this.success(response.message)
          this.items = this.items.filter(item => item.id !== user.id)
          this.items.unshift(user)
          this.totalRows = this.items.length
          this.$refs.addUserInvitationModal.hide()
        } else {
          this.error(response.message)
          this.errors = response.errors
        }
      } catch (err) {
        this.error(err.message)
      } finally {
        this.saving = false
      }
    },
    addUserInvitationModal() {
      this.errors = {}
      this.userModal = {
        user: {
          accountId: this.$route.params.accountId,
          roleId: 0,
        },
        index: -1,
      }
      this.$refs.addUserInvitationModal.show()
    },
    openDeleteUserInvitationModal(item, index) {
      if (this.currentPage > 1) {
        index += ((this.currentPage - 1) * 10)
      }
      this.removeItem = item
      this.removeIndex = index
      this.$refs.removeUserInvitationModal.show()
    },
    async confirmRemoveInvitation() {
      try {
        const response = await AccountsService.rejectInvitation(this.removeItem.id, this.$route.params.accountId)
        if (response) {
          this.items.splice(this.removeIndex, 1)
          this.totalRows = this.items.length
          this.$refs.removeUserInvitationModal.hide()
          this.success(this.$tk('ClientPortal.Account.UserInvitationRemovedSuccessfully'))
        }
      } catch (error) {
        this.error(error.message)
      }
    },
  },
}
</script>
